import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./RSVP.css";

// Use the API endpoint from Terraform outputs
const API_ENDPOINT =
  process.env.REACT_APP_RSVP_API_ENDPOINT ||
  "https://michael-and-ashlynn.com/rsvp";

interface PartyMember {
  full_name: string;
  meal_set_id: string;
  order_index: number;
}

interface Invitee {
  invitee_id: string;
  full_name: string;
  party_members: PartyMember[];
  max_party_size: number;
  contact_email: string;
}

interface MealOption {
  meal_id: string;
  meal_name: string;
  description: string;
  meal_set_id: string;
}

interface MealOptions {
  [key: string]: MealOption[];
}

interface PartyResponse {
  name: string;
  attending: boolean;
  meal_choice: string;
  meal_set_id: string;
}

interface ExistingRSVP {
  party_member_name: string;
  attending: boolean;
  meal_choice: string;
  message: string;
}

type FormStep = "name_lookup" | "rsvp_form" | "success";

const RSVP: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // Form state
  const [currentStep, setCurrentStep] = useState<FormStep>("name_lookup");
  const [fullName, setFullName] = useState("");
  const [invitee, setInvitee] = useState<Invitee | null>(null);
  const [mealOptions, setMealOptions] = useState<MealOptions>({});
  const [responses, setResponses] = useState<PartyResponse[]>([]);
  const [message, setMessage] = useState("");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<{
    [key: number]: boolean;
  }>({});

  // Load meal options when component mounts
  useEffect(() => {
    loadMealOptions();
  }, []);

  // Check for pre-filled names in URL parameters
  useEffect(() => {
    const nameParams = searchParams.getAll('name');
    if (nameParams.length > 0) {
      // Use the first name as the primary name for lookup
      const primaryName = nameParams[0];
      setFullName(primaryName);
      
      // Automatically search for the invitee
      if (primaryName.trim()) {
        // Use setTimeout to ensure handleNameSubmit is defined
        setTimeout(() => {
          searchForInvitee(primaryName);
        }, 100);
      }
    }
  }, [searchParams]);

  const loadMealOptions = async () => {
    try {
      const response = await fetch(`${API_ENDPOINT}/meal-options`);
      const result = await response.json();

      if (response.ok) {
        setMealOptions(result.meal_options);
      } else {
        console.error("Failed to load meal options:", result);
      }
    } catch (error) {
      console.error("Error loading meal options:", error);
    }
  };

  const searchForInvitee = async (name: string) => {
    if (!name.trim()) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_ENDPOINT}/validate-invitee`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: name.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok && result.valid) {
        setInvitee(result.invitee);

        // Sort party members based on their order_index from the database
        const sortedPartyMembers = [...result.invitee.party_members].sort((a, b) => {
          return (a.order_index || 0) - (b.order_index || 0);
        });

        // Initialize responses based on existing RSVPs or default to not attending
        const initialResponses: PartyResponse[] =
          sortedPartyMembers.map((member: PartyMember) => {
            const existingRSVP = result.existing_rsvps?.find(
              (rsvp: ExistingRSVP) =>
                rsvp.party_member_name === member.full_name
            );

            return {
              name: member.full_name,
              attending: existingRSVP?.attending || false,
              meal_choice: existingRSVP?.meal_choice || "",
              meal_set_id: member.meal_set_id,
            };
          });

        // Set the message from existing RSVPs (message is stored once per party)
        const existingMessage =
          result.existing_rsvps?.find(
            (rsvp: ExistingRSVP) => rsvp.message && rsvp.message.trim() !== ""
          )?.message || "";

        setResponses(initialResponses);
        setMessage(existingMessage);
        setCurrentStep("rsvp_form");
      } else {
        setError(
          result.message ||
            "Failed to find your invitation. Please check the spelling or contact us."
        );
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) return;
    await searchForInvitee(fullName);
  };

  const handleAttendanceChange = (index: number, attending: boolean) => {
    const newResponses = [...responses];
    newResponses[index] = {
      ...newResponses[index],
      attending,
      meal_choice: attending ? newResponses[index].meal_choice : "",
    };
    setResponses(newResponses);
  };

  const handleMealChoice = (index: number, mealChoice: string) => {
    const newResponses = [...responses];
    newResponses[index] = {
      ...newResponses[index],
      meal_choice: mealChoice,
    };
    setResponses(newResponses);
    setOpenDropdowns((prev) => ({ ...prev, [index]: false }));
  };

  const toggleDropdown = (index: number) => {
    // Close all other dropdowns when opening a new one
    const newOpenDropdowns: { [key: number]: boolean } = {};
    newOpenDropdowns[index] = !openDropdowns[index];
    setOpenDropdowns(newOpenDropdowns);
  };

  const handleRSVPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitee) return;

    // Validate that all attending members have meal choices
    const attendingMembers = responses.filter((r) => r.attending);
    const missingMeals = attendingMembers.filter((r) => !r.meal_choice);

    if (missingMeals.length > 0) {
      setError("Please select meal choices for all attending party members.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_ENDPOINT}/rsvp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invitee_id: invitee.invitee_id,
          responses,
          message,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setCurrentStep("success");
      } else {
        console.error("RSVP submission failed:", result);
        setError(
          "Sorry, there was an error submitting your RSVP. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting RSVP:", error);
      setError(
        "Sorry, there was an error submitting your RSVP. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const startOver = () => {
    setCurrentStep("name_lookup");
    setFullName("");
    setInvitee(null);
    setResponses([]);
    setMessage("");
    setError("");
  };

  const renderMealOptions = (memberIndex: number) => {
    const response = responses[memberIndex];
    if (!response.attending) return null;

    // Get meals for this person's meal set
    const availableMeals = Object.values(mealOptions)
      .flat()
      .filter((meal) => meal.meal_set_id === response.meal_set_id);

    const selectedMeal = availableMeals.find(
      (meal) => meal.meal_id === response.meal_choice
    );
    const isOpen = openDropdowns[memberIndex];

    return (
      <div className="meal-selection">
        <label htmlFor={`meal-${memberIndex}`}>Meal Choice *</label>
        <div className={`custom-dropdown ${isOpen ? "open" : ""}`}>
          <button
            type="button"
            id={`dropdown-button-${memberIndex}`}
            className={`dropdown-button ${isOpen ? "open" : ""}`}
            onClick={() => toggleDropdown(memberIndex)}
            disabled={isSubmitting}
          >
            <span className="dropdown-text">
              {selectedMeal
                ? `${selectedMeal.meal_name} - ${selectedMeal.description}`
                : "Please select a meal"}
            </span>
            <span className="dropdown-arrow">▼</span>
          </button>
        </div>

        {isOpen && (
          <div className="dropdown-options">
            <div
              className={`dropdown-option ${
                !response.meal_choice ? "selected" : ""
              }`}
              onClick={() => handleMealChoice(memberIndex, "")}
            >
              Please select a meal
            </div>
            {availableMeals.map((meal: MealOption) => (
              <div
                key={meal.meal_id}
                className={`dropdown-option ${
                  response.meal_choice === meal.meal_id ? "selected" : ""
                }`}
                onClick={() => handleMealChoice(memberIndex, meal.meal_id)}
              >
                <div className="meal-name">{meal.meal_name}</div>
                <div className="meal-description">{meal.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section id="rsvp" className="rsvp">
      <div className="container">
        <h2 className="section-title">RSVP</h2>
        {/* <p className="rsvp-subtitle">Please respond by July 15, 2025</p> */}

        {/* Step 1: Name Lookup */}
        {currentStep === "name_lookup" && (
          <form className="rsvp-form" onSubmit={handleNameSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            {isLoading && (
              <div className="alert alert-info">
                <p>Looking up your invitation...</p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="fullName">
                Please enter your name as it appears on the invitation
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g., John Smith or Mr. & Mrs. Smith"
                required
                disabled={isLoading}
              />
              <small className="form-help">
                Enter your name exactly as it appears on your invitation to find
                your party information.
              </small>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? "Checking..." : "Find My Invitation"}
            </button>
          </form>
        )}

        {/* Step 2: RSVP Form */}
        {currentStep === "rsvp_form" && invitee && (
          <form className="rsvp-form" onSubmit={handleRSVPSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="invitee-info">
              <h3>Welcome, {invitee.full_name}!</h3>
              <p>
                Please respond for your party of {invitee.party_members.length}:
              </p>
            </div>

            {responses.map((response, index) => (
              <div
                key={index}
                className={`party-member ${
                  openDropdowns[index] ? "has-open-dropdown" : ""
                }`}
              >
                <h4>{response.name}</h4>

                <div className="form-group">
                  <label>Will {response.name} be attending?</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`attending-${index}`}
                        value="yes"
                        checked={response.attending}
                        onChange={() => handleAttendanceChange(index, true)}
                        disabled={isSubmitting}
                      />
                      Yes,{" "}
                      {response.name === invitee.full_name ? "I'll" : "they'll"}{" "}
                      be there!
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`attending-${index}`}
                        value="no"
                        checked={!response.attending}
                        onChange={() => handleAttendanceChange(index, false)}
                        disabled={isSubmitting}
                      />
                      Sorry,{" "}
                      {response.name === invitee.full_name ? "I" : "they"} can't
                      make it
                    </label>
                  </div>
                </div>

                {renderMealOptions(index)}
              </div>
            ))}

            {responses.some((r) => r.attending) && (
              <div className="form-group">
                <label htmlFor="message">Message for the Couple</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share your excitement or well wishes!"
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={startOver}
                disabled={isSubmitting}
              >
                Start Over
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Send RSVP"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {currentStep === "success" && (
          <div className="rsvp-success">
            <div className="alert alert-success">
              <h3>Thank you for your RSVP!</h3>
              <p>
                We've received your response and are so excited to celebrate
                with you!
              </p>

              {/* {responses.filter(r => r.attending).length > 0 && (
                <div className="rsvp-summary">
                  <h4>Your Response Summary:</h4>
                  <ul>
                    {responses
                      .filter(r => r.attending)
                      .map((response, index) => (
                        <li key={index}>
                          {response.name} - {mealOptions.adult?.find(m => m.meal_id === response.meal_choice)?.meal_name || 
                                          mealOptions.vegan?.find(m => m.meal_id === response.meal_choice)?.meal_name ||
                                          mealOptions.child?.find(m => m.meal_id === response.meal_choice)?.meal_name ||
                                          response.meal_choice}
                        </li>
                      ))}
                  </ul>
                </div>
              )} */}
            </div>

            <button className="btn btn-secondary" onClick={startOver}>
              Make Changes
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RSVP;
